
import React, { useEffect } from 'react';
import { useLayoutEffect, useRef, useState } from 'react'
import Pagination from '../../components/Pagination/Pagination';
import { AddressFind, BulkPushNotificationRequest, EarlyTesterSignup, EarlyTesterSignupStatus } from '@m4a3/api-client';
import { API } from 'aws-amplify';
import { logToDatadog } from '../../services/datadog';
import SelectDropdown from '../../components/SelectDropdown/SelectDropdown';
import { blanchCellId } from '../../constants';
import Badge from '../../components/Badge/Badge';
import RejectionModal from './RejectionModal';
import { newNotification } from 'actions/uiActions';
import { useDispatch } from 'react-redux';
import SuggestedDropPointConfirmationModal from '../Restaurant/components/SuggestedDropPointConfirmationModal';
import { AppDispatch, RootState } from '../../store/configureStore';
import { connect } from 'react-redux';
import { getGeofences } from '../../actions/geofenceActions';
import { getObstacles } from '../../actions/obstacleActions';
import NotifyModal from './NotifyModal';
import { orderfulApi } from '../../services/api-instances';

enum NotificationFilterOptions {
  ALL = 'ALL',
  NOTIFICATION_SENT = 'NOTIFICATION_SENT',
  NOTIFICATION_NOT_SENT = 'NOTIFICATION_NOT_SENT'
}
enum AllOption  {
  ALL = 'ALL',
}
type StatusFilterOptions = AllOption | EarlyTesterSignupStatus

type EarlyTesterListPageProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>

function EarlyTesterListPage({
  geofences,
  obstacles,
  getGeofences,
  getObstacles,
}: EarlyTesterListPageProps) {
  const checkbox = useRef();
  const dispatch = useDispatch();

  const [earlyTesterPage, setEarlyTesterPage] = useState<EarlyTesterSignup[]>([]);
  const [eircodeAddressFindMap, setEircodeAddressFindMap] = useState<{[key: string]: AddressFind}>({})
  const [statusFiler, setStatusFilter] = useState<StatusFilterOptions>(AllOption.ALL);
  const [hasReceivedNotificationFilter, setHasReceivedNotificationFilter] = useState<NotificationFilterOptions>(NotificationFilterOptions.ALL);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalMatchingFilters, setTotalMatchingFilters] = useState<number>(0);
  const [checked, setChecked] = useState(false)
  const [indeterminate, setIndeterminate] = useState(false)
  const [shouldShowRejectionModal, setShouldShowRejectionModal] = useState(false);
  const [selectedTesters, setSelectedTesters] = useState<EarlyTesterSignup[]>([])
  const [showSuggestedDropModalForEircode, setShowSuggestedDropModalForEircode] = useState<string | null>();
  const [showNotificationConfirmationModal, setShowNotificationConfirmationModal] = useState<boolean>(false);

  useLayoutEffect(() => {
    const isIndeterminate = selectedTesters.length > 0 && selectedTesters.length < earlyTesterPage.length;
    setChecked(selectedTesters.length === earlyTesterPage.length && earlyTesterPage.length > 0);
    setIndeterminate(isIndeterminate);
    (checkbox.current as any).indeterminate = isIndeterminate;
  }, [selectedTesters])

  useEffect(() => {
    getGeofences();
    getObstacles(blanchCellId);
  }, []);

  useEffect(() => {
    for (var i = 0; i < earlyTesterPage.length; i++) {
      getAddressInfoForEircode(earlyTesterPage[i].eircode);
    }
  }, [earlyTesterPage])

  useEffect(() => {
    reloadEarlyTesterPage();
  }, [currentPage, statusFiler, hasReceivedNotificationFilter])

  const reloadEarlyTesterPage = async () => {
    const params: {
      page?: number;
      size?: number;
      sort?: string;
      hasBeenNotified?: boolean;
      status?: EarlyTesterSignupStatus;
    } = {
      page: currentPage,
      size: 20,
    };

    if (statusFiler !== AllOption.ALL) {
      params.status = statusFiler
    }
    if (hasReceivedNotificationFilter !== NotificationFilterOptions.ALL) {
      params.hasBeenNotified = hasReceivedNotificationFilter === NotificationFilterOptions.NOTIFICATION_SENT;
    }
    setSelectedTesters([]);
    orderfulApi.getAllEarlyTesterApplicationForCell(blanchCellId, params)
      .then(result => {
        setEarlyTesterPage(result.entities)
        setCurrentPage(result.currentPage);
        setTotalPages(result.totalPages);
        setTotalMatchingFilters(result.totalItems);
      })
      .catch(err => {
        logToDatadog('Error loading page of early testers', { params, err }, 'error')
      });

  };

  const getAddressInfoForEircode = async (eircode: string) => {
    return API.get('api', `/addressfind/locationId/${eircode}`, {})
      .then(result => {
        setEircodeAddressFindMap((currentValue) => ({
          ...currentValue,
          [eircode]: result
        }))
      })
      .catch(err => {
        setEircodeAddressFindMap((currentValue) => ({
          ...currentValue,
          [eircode]: null
        }))
        logToDatadog('Error fetching addres info', {err, eircode}, 'error');
      });
  }

  const bulkApproveForOps = async () => {
    try {
      const earlyTesterUpdates = await Promise.all(selectedTesters.map(tester => orderfulApi.updateEarlyTesterApplication(tester.id, tester.cellId, {
        ...tester,
        status: EarlyTesterSignupStatus.OPS_APPROVED
      })))

      await Promise.all(earlyTesterUpdates
        .filter(tester => eircodeAddressFindMap[tester.eircode] && !eircodeAddressFindMap[tester.eircode].enabled)
        .map(tester => setAddressEnabledProp(eircodeAddressFindMap[tester.eircode].id, true)));

      await reloadEarlyTesterPage();

      dispatch(newNotification({
        variant: 'success',
        message: `Approved ${selectedTesters.length} testers for ops`
      }));
    } catch (err) {
      logToDatadog('error_updating_testers', {err}, 'error')
      dispatch(newNotification({
        variant: 'failure',
        message: 'Failed to update testers. Reload the page'
      }));
    }
  }

  const bulkApproveTesters = async () => {
    try {
      const earlyTesterUpdates = await Promise.all(selectedTesters.map(tester => orderfulApi.updateEarlyTesterApplication(tester.id, tester.cellId, {
        ...tester,
        status: EarlyTesterSignupStatus.FULLY_APPROVED
      })))
      await Promise.all(earlyTesterUpdates
          .filter(tester => eircodeAddressFindMap[tester.eircode] && !eircodeAddressFindMap[tester.eircode].enabled)
          .map(tester => setAddressEnabledProp(eircodeAddressFindMap[tester.eircode].id, true)));
      await reloadEarlyTesterPage();

      dispatch(newNotification({
        variant: 'success',
        message: `Fully approved ${selectedTesters.length} testers`
      }));
    } catch (err) {
      logToDatadog('error_updating_testers', {err}, 'error')
      dispatch(newNotification({
        variant: 'failure',
        message: 'Failed to update testers. Reload the page'
      }));
    }
  };

  const getSelectedTestersWithPushTokens = (): EarlyTesterSignup[] => {
    return selectedTesters.filter(tester => Boolean(tester.pushToken));
  }

  const bulkNotifyTesters = async (title: string, body: string) => {
    try {
      const bulkPushRequest: BulkPushNotificationRequest = {
        title,
        body,
        to: getSelectedTestersWithPushTokens().map(tester => tester.pushToken),
      };

      await orderfulApi.sendBulkPushNotification(blanchCellId, bulkPushRequest);
      await Promise.all(getSelectedTestersWithPushTokens()
        .map(tester => orderfulApi.updateEarlyTesterApplication(tester.id, tester.cellId, {
          ...tester,
          hasBeenNotified: true
        })))
      await reloadEarlyTesterPage();

      dispatch(newNotification({
        variant: 'success',
        message: `Notified ${selectedTesters.length} testers`
      }));
    } catch (err) {
      logToDatadog('error_updating_testers', {err}, 'error')
      dispatch(newNotification({
        variant: 'failure',
        message: 'Failed to notify testers. Reload the page'
      }));
    }
  }

  const bulkRejectTesters = async (note: string) => {
    try {
      const earlyTesterUpdates = await Promise.all(selectedTesters
        .map(tester => {
          const updatedTester: EarlyTesterSignup = {
            ...tester,
            status: EarlyTesterSignupStatus.REJECTED
          };
          if (note) {
            updatedTester.note = note
          }
          return orderfulApi.updateEarlyTesterApplication(tester.id, tester.cellId, updatedTester)
        }))
      await Promise.all(earlyTesterUpdates
          .filter(tester => eircodeAddressFindMap[tester.eircode]?.enabled)
          .map(tester => setAddressEnabledProp(eircodeAddressFindMap[tester.eircode].id, false)));
      await reloadEarlyTesterPage();

      dispatch(newNotification({
        variant: 'success',
        message: `Rejected ${selectedTesters.length} testers`
      }));
    } catch (err) {
      logToDatadog('error_updating_testers', {err}, 'error')
      dispatch(newNotification({
        variant: 'failure',
        message: 'Failed to update testers. Reload the page'
      }));
    }
  }

  const setAddressEnabledProp = (addressID: string, isEnabled: boolean) => {
    return API.put('api', `/addressfind/${addressID}/enabledStatus?isEnabled=${isEnabled}`, {})
  }

  function toggleAll() {
    setSelectedTesters(checked || indeterminate ? [] : earlyTesterPage)
    setChecked(!checked && !indeterminate)
    setIndeterminate(false)
  }

  function getHumanReadableStatus(status: EarlyTesterSignupStatus): string {
    if (status === EarlyTesterSignupStatus.IN_REVIEW) {
      return '🕵️ Awaiting review';
    } else if (status === EarlyTesterSignupStatus.OPS_APPROVED) {
      return '🫡 Ops approved'
    } else if (status === EarlyTesterSignupStatus.FULLY_APPROVED) {
      return '✅ Approved'
    } else if (status === EarlyTesterSignupStatus.REJECTED) {
      return '❌ Rejected'
    }
  }

  return (
    <div className='bg-white h-full'>
      <div className="py-6 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">Early tester applications</h1>
            <p className="mt-2 text-sm text-gray-700">
              Approve for ops to enable their address. Fully approve to allow them to order in the app.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 mt-4">
          <div className='col-span-1 rounded-lg'>
            <SelectDropdown
              options={[
                {
                  displayText: 'All',
                  value: AllOption.ALL
                },
                {
                  displayText: 'In review',
                  value: EarlyTesterSignupStatus.IN_REVIEW,
                },
                {
                  displayText: 'Ops approved',
                  value: EarlyTesterSignupStatus.OPS_APPROVED,
                },
                {
                  displayText: 'Fully approved',
                  value: EarlyTesterSignupStatus.FULLY_APPROVED,
                },
                {
                  displayText: 'Rejected',
                  value: EarlyTesterSignupStatus.REJECTED,
                }
              ]}
              value={statusFiler}
              onChange={(newVal: StatusFilterOptions) => {
                setStatusFilter(newVal)
              }}
              label='Status' />
          </div>
          <div className='col-span-1 rounded-lg'>
            <SelectDropdown
                options={[
                  {
                    displayText: 'All',
                    value: NotificationFilterOptions.ALL,
                  },
                  {
                    displayText: 'Notification sent',
                    value: NotificationFilterOptions.NOTIFICATION_SENT,
                  },
                  {
                    displayText: 'Not sent',
                    value: NotificationFilterOptions.NOTIFICATION_NOT_SENT,
                  }
                ]}
                value={hasReceivedNotificationFilter}
                onChange={(newVal: NotificationFilterOptions) => {
                  setHasReceivedNotificationFilter(newVal)
                  reloadEarlyTesterPage();
                }}
                label='Notified' />
          </div>
          <div className='col-span-1 rounded-lg'>
            <dl className="bg-gray-900/5">
              <div
                className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 bg-white px-4 sm:px-6 xl:px-8"
              >
                <dt className="text-sm font-medium leading-6 text-gray-500">Total # matching filters</dt>
                <dd className="w-full flex-none text-3xl font-medium leading-10 tracking-tight text-gray-900">
                  {totalMatchingFilters}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-6 mt-3 align-middle sm:px-6 lg:px-8">
              <div className="relative">
                {selectedTesters.length > 0 && (
                  <div className="absolute left-14 -top-9 flex h-12 items-center space-x-3 bg-white sm:left-12">
                    <button
                      onClick={bulkApproveForOps}
                      type="button"
                      className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
                    >
                      Approve for ops
                    </button>
                    <button
                      onClick={bulkApproveTesters}
                      type="button"
                      className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
                    >
                      Fully approve
                    </button>
                    <button
                      onClick={() => {
                        setShowNotificationConfirmationModal(true);
                      }}
                      type="button"
                      className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
                    >
                      Send a Notification
                    </button>
                    <button
                      onClick={() => setShouldShowRejectionModal(true)}
                      type="button"
                      className="inline-flex items-center rounded bg-red-50 px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
                    >
                      Reject
                    </button>
                  </div>
                )}
                <table className="min-w-full table-fixed divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th scope="col" className="relative px-7 sm:w-12 sm:px-6">
                        <input
                          type="checkbox"
                          className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                          ref={checkbox}
                          checked={checked}
                          onChange={toggleAll}
                        />
                      </th>
                      <th scope="col" className="min-w-[12rem] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900">
                        Eircode
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Address enabled?
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Free coffee
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Free goody bag
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Notified?
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-3">
                        <span className="sr-only">Edit address</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {earlyTesterPage.map((earlyTester) => (
                      <tr key={earlyTester.id} className={selectedTesters.includes(earlyTester) ? 'bg-gray-50' : undefined}>
                        <td className="relative px-7 sm:w-12 sm:px-6">
                          {selectedTesters.includes(earlyTester) && (
                            <div className="absolute inset-y-0 left-0 w-0.5 bg-blue-600" />
                          )}
                          <input
                            type="checkbox"
                            className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                            value={earlyTester.id}
                            checked={selectedTesters.includes(earlyTester)}
                            onChange={(e) =>
                              setSelectedTesters(
                                e.target.checked
                                  ? [...selectedTesters, earlyTester]
                                  : selectedTesters.filter((p) => p !== earlyTester)
                              )
                            }
                          />
                        </td>
                        <td
                          className={`whitespace-nowrap py-4 pr-3 text-sm font-medium ${selectedTesters.includes(earlyTester) ? 'text-blue-600' : 'text-gray-900'}`}
                        >
                          <div className="font-medium text-gray-900">{earlyTester.eircode}</div>
                          <div className="mt-1 text-gray-500">{new Date(earlyTester.createDate).toLocaleString()}</div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{getHumanReadableStatus(earlyTester.status)}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <Badge colour={eircodeAddressFindMap[earlyTester.eircode]?.enabled ? 'green' : 'red'} displayText={eircodeAddressFindMap[earlyTester.eircode]?.enabled ? 'Enabled' : 'Disabled'} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <Badge colour={earlyTester.enableCoffeePromotion ? 'green' : 'red'} displayText={earlyTester.enableCoffeePromotion ? 'Enabled' : 'Disabled'} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <Badge colour={earlyTester.enableTreatBagPromotion ? 'green' : 'red'} displayText={earlyTester.enableTreatBagPromotion ? 'Enabled' : 'Disabled'} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {earlyTester.pushToken ? <Badge colour={earlyTester.hasBeenNotified ? 'green' : 'red'} displayText={earlyTester.hasBeenNotified ? 'Yes' : 'No'} /> : <Badge colour={'grey'} displayText={'No token'} />}
                        </td>
                        <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                          <button type="button" onClick={() => setShowSuggestedDropModalForEircode(earlyTester.eircode)} className="text-blue-600 hover:text-blue-900">
                            View map
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onNextClick={() => {
              setCurrentPage((page) => page + 1)
            }}
            onPreviousClick={() => {
              setCurrentPage((page) => page - 1)
            }}
            onPageClick={(pageNumber: number) => {setCurrentPage(pageNumber)}} />
        </div>
      </div>
      {shouldShowRejectionModal ? <RejectionModal
        onDismiss={() => setShouldShowRejectionModal(false)}
        onConfirm={(note) => {
          bulkRejectTesters(note);
          setShouldShowRejectionModal(false);
        }} /> : <></> }

      {(showSuggestedDropModalForEircode && eircodeAddressFindMap[showSuggestedDropModalForEircode]) ? (
        <SuggestedDropPointConfirmationModal
          eircode={showSuggestedDropModalForEircode}
          obstacles={obstacles}
          handleClose={(didCreateSuggestedDrops) => {
            getAddressInfoForEircode(showSuggestedDropModalForEircode)
            setShowSuggestedDropModalForEircode(null);
            if (didCreateSuggestedDrops) {
              dispatch(newNotification({
                variant: 'success',
                message: 'Drop points created.'
              }));
            }
          }}
          geofences={geofences}
        />
      ) : null}
      {showNotificationConfirmationModal ? <NotifyModal
        numberOfSelectedTesters={getSelectedTestersWithPushTokens()?.length || 0}
        onConfirm={(title, body) => {
          bulkNotifyTesters(title, body);
          setShowNotificationConfirmationModal(false);
        }}
        onDismiss={() => {
          setShowNotificationConfirmationModal(false);
        }} /> : <></>}
    </div>
  )
}
const mapStateToProps = (state: RootState) => ({
  geofences: state.geofences.geofences,
  obstacles: state.obstacles.list,
});
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getGeofences: () => getGeofences()(dispatch),
  getObstacles: (cellId: number) => getObstacles(cellId)(dispatch),
});
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EarlyTesterListPage);
